import { ProtocolStatus } from '../types';

const PUBLISHED_PROTOCOLS_STORAGE_KEY = 'published_protocols_v1';
export const PUBLISHED_PROTOCOLS_UPDATED_EVENT = 'published-protocols-updated';

const persistPublishedProtocols = (protocols: PublishedProtocol[]): void => {
  localStorage.setItem(PUBLISHED_PROTOCOLS_STORAGE_KEY, JSON.stringify(protocols));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(PUBLISHED_PROTOCOLS_UPDATED_EVENT));
  }
};

export type BuilderRuleType = 'MEAL_TIMING' | 'PORTION_LIMIT' | 'PAIRING' | 'AVOIDANCE' | 'CUSTOM';

export interface PublishedProtocolFood {
  id: string;
  customFoodName: string;
  classification: 'GREEN' | 'YELLOW' | 'RED';
  portion: string;
  notes: string;
}

export interface PublishedProtocolRule {
  id: string;
  ruleType: BuilderRuleType;
  description: string;
  parameters: Record<string, string | number | boolean>;
  isActive: boolean;
}

export type CheckInTaskType = 'MEAL_PHOTO' | 'WEIGHT' | 'BLOOD_PRESSURE' | 'CGM_UPLOAD' | 'SYMPTOM_LOG';

export interface ProtocolCheckInTask {
  id: string;
  frequency: 'daily' | 'weekly' | 'monthly';
  taskType: CheckInTaskType;
  label: string;
  description: string;
  required: boolean;
}

export interface PublishedProtocol {
  id: string;
  patientId: string;
  patientName: string;
  title: string;
  description: string;
  templateId: string;
  status: Extract<ProtocolStatus, 'ACTIVE' | 'ARCHIVED'>;
  sharedWithPatient?: boolean;
  createdDate: string;
  startDate: string;
  endDate?: string;
  templateNotes?: string[];
  classifications: PublishedProtocolFood[];
  rules: PublishedProtocolRule[];
  checkInTasks?: ProtocolCheckInTask[];
}

const isProtocolArray = (value: unknown): value is PublishedProtocol[] => Array.isArray(value);

const normalizeProtocol = (protocol: PublishedProtocol): PublishedProtocol => ({
  ...protocol,
  templateNotes: Array.isArray(protocol.templateNotes)
    ? protocol.templateNotes.filter((n): n is string => typeof n === 'string')
    : typeof protocol.templateNotes === 'string'
    ? [protocol.templateNotes]
    : [],
  classifications: Array.isArray(protocol.classifications)
    ? protocol.classifications
    : [],
  rules: Array.isArray(protocol.rules)
    ? protocol.rules
    : [],
  checkInTasks: Array.isArray(protocol.checkInTasks) ? protocol.checkInTasks : [],
});

export const loadPublishedProtocols = (): PublishedProtocol[] => {
  const raw = localStorage.getItem(PUBLISHED_PROTOCOLS_STORAGE_KEY);
  if (!raw) {
    return [];
  }

  try {
    const parsed = JSON.parse(raw) as unknown;
    return isProtocolArray(parsed) ? parsed.map(normalizeProtocol) : [];
  } catch {
    return [];
  }
};

export const savePublishedProtocol = (protocol: PublishedProtocol): void => {
  const existing = loadPublishedProtocols();
  const next = [normalizeProtocol(protocol), ...existing];
  persistPublishedProtocols(next);
};

export const updatePublishedProtocolShare = (protocolId: string, sharedWithPatient: boolean): void => {
  const existing = loadPublishedProtocols();
  const updated = existing.map((protocol) =>
    protocol.id === protocolId ? { ...protocol, sharedWithPatient } : protocol
  );
  persistPublishedProtocols(updated);
};

export const updatePublishedProtocol = (
  protocolId: string,
  updates: Partial<PublishedProtocol>
): void => {
  const existing = loadPublishedProtocols();
  const updated = existing.map((protocol) =>
    protocol.id === protocolId
      ? normalizeProtocol({ ...protocol, ...updates, id: protocol.id })
      : normalizeProtocol(protocol)
  );
  persistPublishedProtocols(updated);
};
